import { useState, useEffect } from 'react';
import { Col, Row } from 'reactstrap';
import { useSelector } from 'react-redux';

import { useActions } from 'utils/hooks';
import * as routeActions from 'store/routes/actions';

import TableActions from 'components/layouts/table';
import { transformCoords } from 'components/map/helpers';
import Map from 'components/map';
import Table, { useTable } from 'common/components/table';
import NamesInTooltip from '@/library/components/general/NamesInTooltip';
import BasicModal from 'common/components/modals/BasicModal';

import mapIcon from 'assets/svg/components/map/show-map.svg';

const Routes = () => {
  const [map, toggleMap] = useState(false);
  const [route, setRoute] = useState([]);
  const routePoints = useSelector(state => state.routes.routePoints);
  const [getRoutePoints] = useActions([routeActions.getRoutePoints]);
  const table = useTable({
    label: 'routes',
    columns: [
      {
        header: 'Start',
        key: 'start',
        type: 'string',
        sort: false,
        sticky: true,
        left: 0,
        className: 'cell-start-column',
        canFilter: false
      },
      {
        header: 'Total miles',
        key: 'miles_total',
        type: 'number',
        sort: true,
        className: 'ps-2'
      },
      {
        header: 'Eca miles',
        key: 'miles_eca',
        type: 'number',
        sort: true
      },
      {
        header: 'Avoid eca',
        key: 'avoid_seca',
        type: 'boolean',
        options: [
          { label: 'Yes', value: 'true' },
          { label: 'No', value: 'false' }
        ],
        sort: true
      },
      {
        header: 'Avoid piracy',
        key: 'avoid_piracy',
        type: 'boolean',
        options: [
          { label: 'Yes', value: 'true' },
          { label: 'No', value: 'false' }
        ],
        sort: true
      },
      {
        header: 'Avoid tw',
        key: 'avoid_all_tw',
        type: 'boolean',
        options: [
          { label: 'Yes', value: 'true' },
          { label: 'No', value: 'false' }
        ],
        sort: true
      },
      {
        header: 'Avoid routing areas',
        key: 'avoided_areas',
        type: 'string',
        sort: false,
        canFilter: false
      },
      {
        header: 'Use routing areas',
        key: 'used_areas',
        type: 'string',
        sort: false,
        canFilter: false
      },
      {
        header: 'Actual routing areas',
        key: 'actual_routing_areas',
        type: 'string',
        sort: false,
        className: 'pe-7',
        canFilter: false
      },
      {
        header: 'Actions',
        key: 'actions',
        field: 'actions',
        className: 'text-center'
      }
    ]
  });

  const renderCoordinates = (lat, lng) => {
    return (
      <div className="d-flex align-items-center">
        <div>{latLngToCoords(lat)}&nbsp;</div>
        <div className="fw-bold text-purple me-1">{+lat < 0 ? 'S' : 'N'}</div>
        <div>{latLngToCoords(lng)}&nbsp;</div>
        <div className="fw-bold text-purple">{+lat < 0 ? 'W' : 'E'}</div>
      </div>
    );
  };

  const transformRoute = () => {
    let newRoute = [];

    if (routePoints && Array.isArray(routePoints)) {
      routePoints.forEach(point => {
        newRoute = [...newRoute, { lon: point.lon, lat: point.lat }];
      });

      setRoute(transformCoords(newRoute));
    }
  };

  useEffect(() => {
    transformRoute();
  }, [routePoints]);

  return (
    <Row className="routes-table">
      <Col xs={12} className="mb-5">
        <TableActions title="Routes" actions={[{ type: 'search' }]} />
      </Col>
      <Col xs={12}>
        <Table
          loader
          {...table}
          rows={{
            start: data => (
              <div className="d-flex align-items-center">
                <div>{renderCoordinates(data.from_lat, data.from_lon)}</div>
                <div className="coordinates-divider" />
                <div>{renderCoordinates(data.to_lat, data.to_lon)}</div>
              </div>
            ),
            avoid_seca: data => <div>{data.avoid_seca ? 'YES' : 'NO'}</div>,
            avoid_piracy: data => <div>{data.avoid_piracy ? 'YES' : 'NO'}</div>,
            avoid_all_tw: data => <div>{data.avoid_all_tw ? 'YES' : 'NO'}</div>,
            avoided_areas: data =>
              data.avoided_areas.length && data.avoided_areas.length > 0 ? (
                <NamesInTooltip
                  names={data.avoided_areas.map(area => area.name)}
                  target={`avoided-areas-${data.id}`}
                />
              ) : null,
            used_areas: data =>
              data.used_areas.length && data.used_areas.length > 0 ? (
                <NamesInTooltip
                  names={data.used_areas.map(area => area.name)}
                  target={`used-areas-${data.id}`}
                />
              ) : null,
            actual_routing_areas: data =>
              data.areas.length && data.areas.length > 0 ? (
                <NamesInTooltip
                  target={`routing-areas-${data.id}`}
                  names={data.areas.map(area => area.area.name)}
                />
              ) : null,
            actions: {
              options: [
                {
                  label: 'Map',
                  className: 'text-primary',
                  icon: mapIcon,
                  onClick: (_, row) => {
                    toggleMap(true);
                    getRoutePoints({ id: row.id });
                  }
                }
              ]
            }
          }}
        />
      </Col>

      <BasicModal
        isOpen={map}
        toggle={() => toggleMap(!map)}
        header="Destination"
        className="route-map-modal"
        body={<Map className="route-map" route={route} ports={[]} zoomToRoute dottedRoute />}
      />
    </Row>
  );
};

export default Routes;

function latLngToCoords(deg) {
  var d = Math.floor(deg);
  var minfloat = (deg - d) * 60;
  var m = Math.floor(minfloat);
  var secfloat = (minfloat - m) * 60;
  var s = Math.round(secfloat);
  // After rounding, the seconds might become 60. These two
  // if-tests are not necessary if no rounding is done.
  if (s == 60) {
    m++;
    s = 0;
  }
  if (m == 60) {
    d++;
    m = 0;
  }
  return `${d}°${m}′${s}″`;
}
